import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService, AuthResponse } from '../services/auth.service';
import { affiliateService } from '../services/affiliate.service';
import { clearReferral, pendingReferral } from '../utils/referral';

type User = AuthResponse['user'];

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    signup: (name: string, email: string, password: string, company?: string) => Promise<{ success: boolean; error?: string }>;
    logout: () => void;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshUser = useCallback(async () => {
        try {
            const response = await authService.getProfile();
            if (response.success && response.data) {
                setUser({
                    id: response.data.id,
                    email: response.data.email,
                    fullName: response.data.fullName,
                    avatar: response.data.avatar,
                    credits: response.data.credits,
                });
            } else {
                setUser(null);
            }
        } catch (error: any) {
            // Only a 401 means the session is gone. A restart, timeout or 5xx keeps
            // the cached user so a blip in core-api doesn't bounce people to /login.
            if (error?.response?.status === 401) {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                setUser(null);
            } else {
                setUser(authService.getUser());
            }
        }
    }, []);

    useEffect(() => {
        const initAuth = async () => {
            const token = authService.getToken();
            if (token) {
                // Only consider authenticated if we have a valid token
                await refreshUser();
            } else {
                // No token means not authenticated - clear any stale user data
                setUser(null);
            }
            setIsLoading(false);
        };
        initAuth();
    }, [refreshUser]);

    const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await authService.login({ email, password });
            if (response.access_token && response.user) {
                setUser(response.user);
                return { success: true };
            }
            return { success: false, error: 'Login failed' };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || 'Login failed' };
        }
    };

    const signup = async (name: string, email: string, password: string, company?: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await authService.signup({ email, password, fullName: name, company });
            if (response.access_token && response.user) {
                setUser(response.user);
                return { success: true };
            }
            return { success: false, error: 'Signup failed' };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || 'Signup failed' };
        }
    };

    const logout = () => {
        authService.logout();
        setUser(null);
    };

    // A pending affiliate code is sent once the user is signed in, whichever way
    // they signed in (form or OAuth). The server ignores it for accounts older
    // than 24 h, so it's safe to fire on every login. Kept on network failure so
    // the next session retries; cleared once the server has answered.
    const userId = user?.id;
    useEffect(() => {
        if (!userId) return;
        const code = pendingReferral();
        if (!code) return;
        affiliateService.attribute(code).then(clearReferral).catch(() => undefined);
    }, [userId]);

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        logout,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

